"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import {
	Coins,
	LogOut,
	MapPin,
	MessageCircleQuestion,
	Package,
	Sparkles,
	UserIcon,
	UserRound,
	Users,
} from "lucide-react";
import { UserAvatar } from "./components/user-avatar";
import { type UserDetailsFragment } from "@/gql/graphql";
import { logout } from "@/app/actions";
import { LinkWithChannel } from "@/ui/atoms/link-with-channel";
import { cn } from "@/lib/utils";
import { navigationExitDuration } from "../navigation-motion";

type Props = {
	user: UserDetailsFragment | null;
	unavailable?: boolean;
};

type ItemConfig = {
	icon: React.ComponentType<{ className?: string }>;
	labelKey: "myAccount" | "myOrders" | "referrals" | "support" | "addresses" | "bergdorfPoints";
	href: string;
};

const ITEMS: ItemConfig[] = [
	{ icon: UserRound, labelKey: "myAccount", href: "/account" },
	{ icon: Package, labelKey: "myOrders", href: "/orders" },
	{ icon: Users, labelKey: "referrals", href: "/account/referrals" },
	{ icon: MessageCircleQuestion, labelKey: "support", href: "/account/support" },
	{ icon: MapPin, labelKey: "addresses", href: "/account/addresses" },
	{ icon: Sparkles, labelKey: "bergdorfPoints", href: "/account/loyalty" },
];

/** Guest dropdown: sign-in for existing customers, registration invite with Bergdorf Points. */
function GuestPanel({ onNavigate }: { onNavigate: () => void }) {
	const t = useTranslations("nav");
	const tAuth = useTranslations("auth");
	const tSignup = useTranslations("signup");
	const params = useParams<{ locale?: string; channel?: string }>();
	const base = `/${params.locale ?? "en"}/${params.channel ?? "eu"}`;

	const benefits = [
		{ icon: Sparkles, label: t("signupBonusHint") },
		{ icon: Coins, label: t("benefitPoints") },
		{ icon: MessageCircleQuestion, label: t("benefitSupport") },
	];

	return (
		<div className="px-2 pb-2 pt-1">
			<p className="px-1 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
				{t("accountAria")}
			</p>
			<LinkWithChannel
				href={`/login?next=${encodeURIComponent(`${base}/account`)}`}
				onClick={onNavigate}
				className="hover:bg-primary/90 flex w-full items-center justify-center rounded-xl bg-primary px-3 py-2.5 text-[13.5px] font-semibold text-primary-foreground transition-colors"
			>
				{tAuth("signIn")}
			</LinkWithChannel>
			<div className="my-3 flex items-center gap-2.5 px-1" aria-hidden>
				<span className="border-foreground/[0.08] h-px flex-1 border-t" />
				<span className="text-[11px] font-medium text-muted-foreground">{t("newHere")}</span>
				<span className="border-foreground/[0.08] h-px flex-1 border-t" />
			</div>
			<ul className="flex flex-col gap-1.5 px-1">
				{benefits.map(({ icon: Icon, label }) => (
					<li key={label} className="text-foreground/85 flex items-center gap-2.5 text-[12.5px]">
						<span className="bg-primary/10 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg">
							<Icon className="h-3 w-3 text-primary" />
						</span>
						{label}
					</li>
				))}
			</ul>
			<LinkWithChannel
				href="/signup"
				onClick={onNavigate}
				className="border-primary/30 hover:bg-primary/[0.06] mt-3 flex w-full items-center justify-center rounded-xl border bg-transparent px-3 py-2.5 text-[13.5px] font-semibold text-primary transition-colors"
			>
				{tSignup("createAccount")}
			</LinkWithChannel>
		</div>
	);
}

export function UserMenu({ user, unavailable = false }: Props) {
	const t = useTranslations("nav");
	const isAuthed = user !== null;

	const [open, setOpen] = useState(false);
	const [rendered, setRendered] = useState(false);
	const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const cancelTimers = useCallback(() => {
		if (openTimer.current) clearTimeout(openTimer.current);
		if (closeTimer.current) clearTimeout(closeTimer.current);
		openTimer.current = null;
		closeTimer.current = null;
	}, []);

	const show = useCallback(() => {
		cancelTimers();
		if (exitTimer.current) clearTimeout(exitTimer.current);
		exitTimer.current = null;
		setRendered(true);
		setOpen(true);
	}, [cancelTimers]);

	const hide = useCallback(
		(returnFocus = false) => {
			cancelTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
			if (returnFocus) triggerRef.current?.focus();
			setOpen(false);
			const duration = navigationExitDuration();
			if (duration === 0) {
				setRendered(false);
				return;
			}
			exitTimer.current = setTimeout(() => {
				setRendered(false);
				exitTimer.current = null;
			}, duration);
		},
		[cancelTimers],
	);

	const scheduleOpen = useCallback(() => {
		cancelTimers();
		if (exitTimer.current) clearTimeout(exitTimer.current);
		exitTimer.current = null;
		openTimer.current = setTimeout(show, 150);
	}, [cancelTimers, show]);

	const scheduleClose = useCallback(() => {
		cancelTimers();
		closeTimer.current = setTimeout(() => {
			if (panelRef.current?.contains(document.activeElement)) return;
			hide();
		}, 120);
	}, [cancelTimers, hide]);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			event.preventDefault();
			hide(true);
		};
		const onPointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) hide();
		};
		document.addEventListener("keydown", onKey);
		document.addEventListener("pointerdown", onPointerDown);
		return () => {
			document.removeEventListener("keydown", onKey);
			document.removeEventListener("pointerdown", onPointerDown);
		};
	}, [open, hide]);

	useEffect(
		() => () => {
			cancelTimers();
			if (exitTimer.current) clearTimeout(exitTimer.current);
		},
		[cancelTimers],
	);

	if (unavailable) {
		return (
			<LinkWithChannel
				href="/account"
				aria-label={t("accountAria")}
				className="inline-flex h-[38px] w-[38px] items-center justify-center rounded-full hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
			>
				<UserIcon className="text-foreground/80 h-5 w-5" aria-hidden />
			</LinkWithChannel>
		);
	}

	return (
		<div
			ref={rootRef}
			className="relative isolate"
			onMouseEnter={scheduleOpen}
			onMouseLeave={scheduleClose}
			onBlur={(event) => {
				if (!event.currentTarget.contains(event.relatedTarget)) hide();
			}}
		>
			<button
				ref={triggerRef}
				type="button"
				onClick={() => (open ? hide() : show())}
				aria-expanded={open}
				aria-haspopup={isAuthed ? "menu" : "dialog"}
				aria-controls="header-account-menu"
				className="focus:outline-hidden hover:bg-foreground/[0.06] relative inline-flex h-[38px] w-[38px] items-center justify-center rounded-full bg-transparent transition-colors focus:ring-2 focus:ring-ring"
			>
				<span className="sr-only">{t("accountAria")}</span>
				{isAuthed ? (
					<UserAvatar user={user} />
				) : (
					<UserIcon className="text-foreground/80 h-5 w-5" aria-hidden />
				)}
			</button>

			{rendered && (
				<div
					id="header-account-menu"
					ref={panelRef}
					role={isAuthed ? "menu" : "dialog"}
					aria-label={t("accountAria")}
					inert={!open}
					aria-hidden={!open}
					onMouseEnter={cancelTimers}
					onMouseLeave={scheduleClose}
					onClickCapture={(event) => {
						if (!open) {
							event.preventDefault();
							event.stopPropagation();
						}
					}}
					onKeyDownCapture={(event) => {
						if (!open) {
							event.preventDefault();
							event.stopPropagation();
						}
					}}
					className={cn(
						"t-nav-dropdown absolute right-0 top-[calc(100%+10px)] z-[100] w-[260px]",
						"border-foreground/[0.08] rounded-[24px] border bg-card",
						"p-2 shadow-[0_24px_60px_rgba(14,23,38,0.14),0_2px_6px_rgba(14,23,38,0.06)]",
						open ? "is-open" : "is-closing",
					)}
					data-origin="top-right"
				>
					{isAuthed ? (
						<>
							<div className="border-foreground/[0.06] mb-1 border-b px-3 py-2">
								<p className="truncate text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
									{user.email}
								</p>
							</div>
							<div className="flex flex-col">
								{ITEMS.map(({ icon: Icon, labelKey, href }) => (
									<LinkWithChannel
										key={labelKey}
										href={href}
										onClick={() => hide()}
										className="text-foreground/85 hover:bg-foreground/[0.06] group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-[13.5px] font-medium transition-colors hover:text-foreground"
									>
										<span className="bg-primary/10 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
											<Icon className="h-3.5 w-3.5 text-primary" />
										</span>
										{t(labelKey)}
									</LinkWithChannel>
								))}
							</div>
							<form action={logout} className="border-foreground/[0.06] mt-1 border-t pt-1">
								<button
									type="submit"
									className="hover:bg-foreground/[0.06] flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
								>
									<span className="bg-foreground/[0.05] flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
										<LogOut className="h-3.5 w-3.5" />
									</span>
									Log Out
								</button>
							</form>
						</>
					) : (
						<GuestPanel onNavigate={() => hide()} />
					)}
				</div>
			)}
		</div>
	);
}
